import React, { Suspense } from 'react';
import { Route, Navigate } from "react-router-dom";
import { peekDemoBypass, DemoBypassGuard, landingFor, isAdminRole } from './helpers';
import LoadingFallback from './LoadingFallback';
import ErrorBoundary from '../../components/ErrorBoundary';
import ErrorFallback from '../../components/shared/ErrorFallback';
import lazyWithRetry from '../../utils/lazyWithRetry';
import { usePermissions } from '../../hooks/usePermissions';

import { BillingRoutes } from './billingRoutes';
import { LeadRoutes } from './leadRoutes';

// Helper for standard user-only routes (redirects clients to /client, non-users to login)
const UserRoute = ({ user, isClientUser = (_user: unknown) => true, children }) => {
  if (user === null) return <div>Checking login...</div>;
  if (!user) return <Navigate to="/login" />;
  if (isClientUser(user)) return <Navigate to="/client" />;
  return children;
};

// Helper for admin-only routes — permission-based with role fallback for demo mode
const AdminRoute = ({ user, userHasRole, isClientUser, requiredPermission = 'settings.view', allowedRoles = ['admin', 'admin_provider', 'clinical_director'], children }) => {
  const { hasPermission, loading } = usePermissions();
  if (user === null || loading) return <div>Checking login...</div>;
  if (!user) return <Navigate to="/login" />;
  const isAllowed = hasPermission(requiredPermission) ||
    allowedRoles.some(role => userHasRole(user, role)) ||
    (user.demo_mode && ['admin', 'admin_provider', 'clinical_director'].includes(user.character_type));
  if (isAllowed) return children;
  if (isClientUser(user)) return <Navigate to="/client" />;
  return <Navigate to={landingFor(user)} />;
};

// Lazy-loaded pages — use lazyWithRetry to auto-recover from chunk load
// failures after deployments (stale cached HTML referencing old chunk hashes).
const PatientManagement = lazyWithRetry(() => import('../../features/patient-management/PatientManagement'));
const AdminChat = lazyWithRetry(() => import('../../pages/AdminChat'));
const AdminUserManagement = lazyWithRetry(() => import('../../pages/AdminUserManagement'));
const AdminSettings = lazyWithRetry(() => import('../../features/admin-settings/AdminSettings'));
const AdminSetupGuides = lazyWithRetry(() => import('../../pages/AdminSetupGuides'));
const AdminOnboardingPage = lazyWithRetry(() => import('../../pages/AdminOnboardingPage'));
const AdminAuditLogs = lazyWithRetry(() => import('../../pages/AdminAuditLogs'));
const AdminSupportDashboard = lazyWithRetry(() => import('../../pages/AdminSupportDashboard'));
const ComplianceDashboard = lazyWithRetry(() => import('../../pages/ComplianceDashboard'));
// AdminReports removed - consolidated into KPI Dashboard (ProviderAnalytics)
const AdminPayroll = lazyWithRetry(() => import('../../pages/AdminPayroll'));
const AdminFormBuilder = lazyWithRetry(() => import('../../pages/AdminFormBuilder'));
const CPTMappingsPage = lazyWithRetry(() => import('../../pages/admin/CPTMappingsPage'));
const ComponentShowcase = lazyWithRetry(() => import('../../pages/ComponentShowcase'));
const MobileAnalyticsMockups = lazyWithRetry(() => import('../../pages/MobileAnalyticsMockups'));
const NewDashboardPage = lazyWithRetry(() => import('../../pages/NewDashboardPage'));
const ProviderAnalytics = lazyWithRetry(() => import('../../features/analytics/AnalyticsPage'));
const ProviderScorecard = lazyWithRetry(() => import('../../features/provider-scorecard/ProviderScorecardPage'));
const ProviderHealth = lazyWithRetry(() => import('../../features/provider-health/ProviderHealthPage'));
const PracticeHealth = lazyWithRetry(() => import('../../features/practice-kpi/PracticeKPIDashboardPage'));
const PracticeHealthNewWorkFunnel = lazyWithRetry(() => import('../../features/practice-kpi/PracticeHealthNewWorkFunnelPage'));
const PracticeKPILeadSourceDrilldown = lazyWithRetry(() => import('../../features/practice-kpi/PracticeKPILeadSourceDrilldownPage'));
const SupervisionPage = lazyWithRetry(() => import('../../pages/SupervisionPage'));
const SupervisorCaseloadPage = lazyWithRetry(() => import('../../pages/SupervisorCaseloadPage'));
const StudentCaseloadPage = lazyWithRetry(() => import('../../pages/StudentCaseloadPage'));
const FormAssignments = lazyWithRetry(() => import('../../pages/FormAssignments'));
const ChartReviewQueue = lazyWithRetry(() => import('../../pages/ChartReviewQueue'));
// TimesheetPage and MyPayroll removed — /timesheets and /my-payroll redirect to /my-work
// VoiceMode removed — /voice now redirects to /drive-mode
const AdminOutcomesPage = lazyWithRetry(() => import('../../features/outcomes/AdminOutcomesPage'));
const MigrationWorkspacePage = lazyWithRetry(() => import('../../features/migration/MigrationWorkspacePage'));
const FrozenWorkspaceImport = lazyWithRetry(() => import('../../features/migration/FrozenWorkspaceImport'));
const MigrationWizard = lazyWithRetry(() => import('../../features/migration/MigrationWizard'));
const ImportHistoryPage = lazyWithRetry(() => import('../../pages/ImportHistoryPage'));
const PermissionViewerPage = lazyWithRetry(() => import('../../features/admin/PermissionViewerPage'));
const MergeRecordsPage = lazyWithRetry(() => import('../../features/admin/merges/MergeRecordsPage'));
const PlatformOwnerDashboard = lazyWithRetry(() => import('../../platform-owner/PlatformOwnerDashboard'));
const SubscriptionSignUpPage = lazyWithRetry(() => import('../../pages/SubscriptionSignUpPage'));
const AdminEventManagement = lazyWithRetry(() => import('../../features/events/AdminEventManagement'));
const SetupWizard = lazyWithRetry(() => import('../../features/setup-wizard/SetupWizard'));
const ClientMigrationDashboard = lazyWithRetry(() => import('../../features/client-migration/ClientMigrationDashboard'));

/** Admin Routes - Routes accessible to admin users */
export function AdminRoutes({ user, userHasRole, isClientUser = (_user: unknown) => true }) {
  return (
    <>
              {/* Admin Base Route */}
              <Route
                path="/admin"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : (user && peekDemoBypass('/admin', ['admin', 'admin_provider', 'clinical_director']))
                    ? <DemoBypassGuard><Navigate to="/admin/dashboard" /></DemoBypassGuard>
                    : !user
                    ? <DemoBypassGuard><Navigate to="/login" /></DemoBypassGuard>
                    : (() => {
                        const isDemo = !!user?.demo_mode;
                        const isDemoAdmin = isDemo && ['admin', 'admin_provider', 'clinical_director'].includes(user?.character_type);
                        const isDemoClient = isDemo && ['client','parent_of_client','part_of_couple'].includes(user?.character_type);
                        const isClient = userHasRole(user, 'client') || isDemoClient;
                        const isAdmin = isAdminRole(user) || isDemoAdmin;
                        if (isAdmin) return <Navigate to="/admin/dashboard" />;
                        if (isClient) return <Navigate to="/client" />;
                        return <Navigate to={landingFor(user)} />;
                      })()
                }
              />

              {/* Admin Dashboard */}
              <Route
                path="/admin/dashboard"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <UserRoute user={user} isClientUser={isClientUser}>
                      <ErrorBoundary fallback={<ErrorFallback title="Dashboard" />}>
                        <NewDashboardPage />
                      </ErrorBoundary>
                    </UserRoute>
                  </Suspense>
                }
              />

              <Route
                path="/admin/practice-kpis/lead-sources"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminRoute
                      user={user}
                      userHasRole={userHasRole}
                      isClientUser={isClientUser}
                      requiredPermission="reports.view"
                    >
                      <PracticeKPILeadSourceDrilldown />
                    </AdminRoute>
                  </Suspense>
                }
              />

              <Route
                path="/admin/practice-health"
                element={
                  <AdminRoute
                    user={user}
                    userHasRole={userHasRole}
                    isClientUser={isClientUser}
                    requiredPermission="reports.view"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <ErrorBoundary fallback={<ErrorFallback title="Practice Health" />}>
                        <PracticeHealth />
                      </ErrorBoundary>
                    </Suspense>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/practice-health/new-work"
                element={
                  <AdminRoute
                    user={user}
                    userHasRole={userHasRole}
                    isClientUser={isClientUser}
                    requiredPermission="reports.view"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <ErrorBoundary fallback={<ErrorFallback title="New Work funnel" />}>
                        <PracticeHealthNewWorkFunnel />
                      </ErrorBoundary>
                    </Suspense>
                  </AdminRoute>
                }
              />
              
              {/* /admin/kpi redirect is below with other legacy analytics redirects */}
              
              <Route
                path="/admin/chat"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                      <ErrorBoundary fallback={<ErrorFallback title="Analytics Assistant" />}>
                        <AdminChat />
                      </ErrorBoundary>
                    </AdminRoute>
                  </Suspense>
                }
              />
              <Route path="/demo-admin" element={<Navigate to="/admin/dashboard" />} />
              <Route path="/admin/calendar" element={<Navigate to="/calendar" replace />} />
              <Route path="/admin/appointments" element={<Navigate to="/calendar" replace />} />
              {/* Admin Staff Management */}
              <Route
                path="/admin/staff"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user && (isAdminRole(user) || userHasRole(user, 'supervisor'))
                    ? <AdminUserManagement />
                    : isClientUser(user) ? <Navigate to="/client" /> : <Navigate to={landingFor(user)} />
                }
              />
              {/* Alias: /admin/users -> /admin/staff */}
              <Route path="/admin/users" element={<Navigate to="/admin/staff" replace />} />
              {/* Alias: /admin/team -> /admin/staff */}
              <Route path="/admin/team" element={<Navigate to="/admin/staff" replace />} />

              {/* Admin Clients List */}
              <Route
                path="/admin/clients"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <PatientManagement />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/audit-logs"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <AdminAuditLogs />
                  </AdminRoute>
                }
              />

              {/* Support - Operational health and request tracing (PHI-free) */}
              <Route
                path="/admin/support"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <AdminSupportDashboard />
                  </AdminRoute>
                }
              />

              {/* Permission Viewer - Read-only view of permissions and role templates */}
              <Route
                path="/admin/permissions"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <PermissionViewerPage />
                  </AdminRoute>
                }
              />

              {/* Data Merges - Admin tool for merging duplicate patient/practitioner records */}
              <Route
                path="/admin/merges"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <MergeRecordsPage />
                  </AdminRoute>
                }
              />

              {/* Platform Owner Dashboard - SaaS owner only, NOT accessible from demo */}
              <Route
                path="/platform-owner"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user && userHasRole(user, 'platform_owner') && (!user.demo_mode || user.is_dev)
                    ? <PlatformOwnerDashboard />
                    : user && user.demo_mode
                    ? <Navigate to="/admin/dashboard" />
                    : !user
                    ? <Navigate to="/login" />
                    : <Navigate to={landingFor(user)} />
                }
              />

              <Route
                path="/admin/settings"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <AdminSettings />
                  </AdminRoute>
                }
              />
              <Route path="/admin/billing-settings" element={<Navigate to="/admin/settings?tab=billing" replace />} />

              <Route
                path="/admin/setup-guides"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <AdminSetupGuides />
                  </AdminRoute>
                }
              />

              {/* Admin Onboarding / Getting Started */}
              <Route
                path="/admin/onboarding"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user && isAdminRole(user)
                    ? <AdminOnboardingPage />
                    : isClientUser(user) ? <Navigate to="/client" /> : <Navigate to={landingFor(user)} />
                }
              />
              {/* Alias: /admin/getting-started -> /admin/onboarding */}
              <Route path="/admin/getting-started" element={<Navigate to="/admin/onboarding" replace />} />

              {/* Setup Wizard - Full onboarding wizard for new organizations */}
              <Route
                path="/admin/setup-wizard"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <SetupWizard />
                  </AdminRoute>
                }
              />


              {/* Migration Wizard */}
              <Route
                path="/admin/migration/history"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <ImportHistoryPage />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/migration"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <MigrationWorkspacePage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/migration/:workspaceId/import"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <FrozenWorkspaceImport />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/migration/legacy"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <MigrationWizard />
                  </AdminRoute>
                }
              />

              {/* Import Clients - alias for Migration Wizard */}
              <Route
                path="/admin/import-clients"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <MigrationWizard />
                  </AdminRoute>
                }
              />

              {/* Client Migration Dashboard */}
              <Route
                path="/admin/client-migration"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <ClientMigrationDashboard />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/compliance"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <ComplianceDashboard />
                  </AdminRoute>
                }
              />

              {/* Canonical analytics route — role-based rendering handled internally */}
              <Route
                path="/analytics"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <UserRoute user={user} isClientUser={isClientUser}>
                      <ErrorBoundary fallback={<ErrorFallback title="Analytics" />}>
                        <ProviderAnalytics />
                      </ErrorBoundary>
                    </UserRoute>
                  </Suspense>
                }
              />

              <Route
                path="/admin/provider-scorecard"
                element={
                  <AdminRoute
                    user={user}
                    userHasRole={userHasRole}
                    isClientUser={isClientUser}
                    requiredPermission="reports.view"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <ErrorBoundary fallback={<ErrorFallback title="Provider Scorecard" />}>
                        <ProviderScorecard />
                      </ErrorBoundary>
                    </Suspense>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/provider-health/:practitionerId"
                element={
                  <AdminRoute
                    user={user}
                    userHasRole={userHasRole}
                    isClientUser={isClientUser}
                    requiredPermission="reports.view"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <ErrorBoundary fallback={<ErrorFallback title="Provider Health" />}>
                        <ProviderHealth />
                      </ErrorBoundary>
                    </Suspense>
                  </AdminRoute>
                }
              />

              {/* Legacy analytics routes — all redirect to /analytics */}
              <Route path="/admin/kpi" element={<Navigate to="/analytics" replace />} />
              <Route path="/admin/provider-analytics" element={<Navigate to="/analytics" replace />} />
              <Route path="/provider-analytics" element={<Navigate to="/analytics" replace />} />
              <Route path="/admin/analytics" element={<Navigate to="/analytics" replace />} />
              <Route path="/admin/reports" element={<Navigate to="/analytics" replace />} />
              <Route path="/admin/revenue" element={<Navigate to="/analytics?tab=financial" replace />} />

              <Route
                path="/admin/outcomes"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <AdminOutcomesPage />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/payroll"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser} requiredPermission="payroll.manage" allowedRoles={[]}>
                    <AdminPayroll />
                  </AdminRoute>
                }
              />

              {/* All staff can access forms — permission model is enforced per-item */}
              <Route
                path="/admin/forms"
                element={
                  <UserRoute user={user} isClientUser={isClientUser}>
                    <AdminFormBuilder />
                  </UserRoute>
                }
              />

              {/* Alias: /admin/forms-paperwork -> /admin/forms */}
              <Route path="/admin/forms-paperwork" element={<Navigate to="/admin/forms" replace />} />

              {/* Legacy packet manager routes — both live under Booking & Intake now */}
              <Route
                path="/admin/screening-packets"
                element={<Navigate to="/admin/settings?tab=appointments&bi=screening" replace />}
              />
              <Route
                path="/admin/paperwork-packets"
                element={<Navigate to="/admin/settings?tab=appointments&bi=paperwork" replace />}
              />

              <Route
                path="/admin/form-assignments"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <FormAssignments />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/chart-review"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser} allowedRoles={['admin', 'clinical_director', 'supervisor']}>
                    <ChartReviewQueue />
                  </AdminRoute>
                }
              />

              {/* Task Automation — redirect to unified Communications & Automations page */}
              <Route
                path="/admin/task-automation"
                element={<Navigate to="/admin/settings?tab=communications" replace />}
              />

              {/* Notifications & Reminders — redirect to unified Communications page */}
              <Route
                path="/admin/notifications-reminders"
                element={<Navigate to="/admin/settings?tab=communications" replace />}
              />

              {/* Communications — redirect to unified Communications settings tab */}
              <Route
                path="/admin/communications"
                element={<Navigate to="/admin/settings?tab=communications" replace />}
              />

              {/* Provider Rates — redirect to settings sub-tab */}
              <Route
                path="/admin/provider-rates"
                element={<Navigate to="/admin/settings?tab=provider-rates" replace />}
              />

              <Route
                path="/supervision"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user && (userHasRole(user, 'supervisor') || isAdminRole(user))
                    ? <SupervisionPage />
                    : isClientUser(user) ? <Navigate to="/client" /> : <Navigate to={landingFor(user)} />
                }
              />

              <Route
                path="/supervision/caseload"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user && (userHasRole(user, 'supervisor') || isAdminRole(user))
                    ? <Suspense fallback={<LoadingFallback />}><SupervisorCaseloadPage /></Suspense>
                    : isClientUser(user) ? <Navigate to="/client" /> : <Navigate to={landingFor(user)} />
                }
              />

              <Route
                path="/my-caseload"
                element={
                  <UserRoute user={user} isClientUser={isClientUser}>
                    <Suspense fallback={<LoadingFallback />}><StudentCaseloadPage /></Suspense>
                  </UserRoute>
                }
              />

              {/* Redirect legacy /caseload path to /my-caseload */}
              <Route path="/caseload" element={<Navigate to="/my-caseload" replace />} />

              <Route
                path="/admin/cpt-mappings"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <CPTMappingsPage />
                  </AdminRoute>
                }
              />

              {/* /timesheets and /my-payroll are handled by therapistRoutes (redirect to /my-work) */}
              <Route path="/voice" element={<Navigate to="/drive-mode" replace />} />

              {/* Subscription Sign-Up */}
              <Route
                path="/subscription/signup"
                element={<SubscriptionSignUpPage />}
              />

              {/* Event Management */}
              <Route
                path="/admin/events"
                element={
                  <AdminRoute user={user} userHasRole={userHasRole} isClientUser={isClientUser}>
                    <AdminEventManagement />
                  </AdminRoute>
                }
              />

              {/* Billing Routes (extracted to billingRoutes.jsx) */}
              {BillingRoutes({ user, userHasRole, isClientUser })}

              {/* Lead Routes (extracted to leadRoutes.jsx) */}
              {LeadRoutes({ user, userHasRole })}

              {/* Component Showcase */}
              <Route
                path="/component-showcase"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user && userHasRole(user, 'dev')
                    ? <ComponentShowcase />
                    : user
                    ? <Navigate to={landingFor(user)} />
                    : <Navigate to="/login" />
                }
              />
              
              {/* Mobile Analytics Mockups */}
              <Route
                path="/mobile-analytics-mockups"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? <MobileAnalyticsMockups />
                    : <Navigate to="/login" />
                }
              />
    </>
  );
}
